/**
 * Account Hierarchy Builder Component
 * 
 * Creates and manages GL account hierarchies for consolidation reporting
 */

import React, { useState, useEffect } from 'react';
import { accountHierarchyAPI, chartOfAccountsAPI } from '../services/accountingService';
import {
  ChartBar, Tree, PlusCircle, ArrowsClockwise, DownloadSimple, TrendUp,
  Lightbulb, Prohibit, MagnifyingGlass,
} from '@phosphor-icons/react';
import '../styles/accountHierarchyBuilder.css';

function AccountHierarchyBuilder() {
  const [activeHierarchy, setActiveHierarchy] = useState(null);
  const [hierarchyTree, setHierarchyTree] = useState(null);
  const [consolidatedReport, setConsolidatedReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [glAccounts, setGlAccounts] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showNodeForm, setShowNodeForm] = useState(false);
  const [formData, setFormData] = useState({
    hierarchy_name: '',
    hierarchy_type: 'organizational',
    description: '',
  });
  const [nodeData, setNodeData] = useState({
    node_name: '',
    node_code: '',
    level: 'detail',
    gl_account_id: '',
    parent_node_id: '',
  });

  useEffect(() => {
    fetchGlAccounts();
  }, []);

  const fetchGlAccounts = async () => {
    try {
      const accounts = await chartOfAccountsAPI.getAllAccounts();
      setGlAccounts(accounts || []);
    } catch (err) {
      console.error('Error fetching accounts:', err);
    }
  };

  const handleCreateHierarchy = async (e) => {
    e.preventDefault();
    try {
      const result = await accountHierarchyAPI.createHierarchy(formData);
      setActiveHierarchy({
        id: result.hierarchy_id,
        hierarchy_name: result.hierarchy_name,
        hierarchy_type: result.hierarchy_type,
      });
      setFormData({
        hierarchy_name: '',
        hierarchy_type: 'organizational',
        description: '',
      });
      setShowCreateForm(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateNode = async (e) => {
    e.preventDefault();
    if (!activeHierarchy) {
      setError('Select a hierarchy first');
      return;
    }
    try {
      await accountHierarchyAPI.createNode({
        hierarchy_id: activeHierarchy.id,
        ...nodeData,
      });
      setNodeData({
        node_name: '',
        node_code: '',
        level: 'detail',
        gl_account_id: '',
        parent_node_id: '',
      });
      setShowNodeForm(false);
      loadHierarchyTree(activeHierarchy.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const loadHierarchyTree = async (hierarchyId) => {
    try {
      setLoading(true);
      const tree = await accountHierarchyAPI.getHierarchyTree(hierarchyId);
      setHierarchyTree(tree);

      const report = await accountHierarchyAPI.getConsolidatedReport(hierarchyId);
      setConsolidatedReport(report);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRollup = async (hierarchyId) => {
    try {
      setLoading(true);
      await accountHierarchyAPI.rollupAllNodes(hierarchyId);
      loadHierarchyTree(hierarchyId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (hierarchyId) => {
    try {
      const result = await accountHierarchyAPI.exportHierarchy(hierarchyId, 'json');
      console.log('Export:', result);
    } catch (err) {
      setError(err.message);
    }
  };

  const renderTreeNode = (node, level = 0) => {
    if (!node) return null;
    
    const paddingLeft = (level || 0) * 20;
    const balance = node.balance ? parseFloat(node.balance).toFixed(2) : '0.00';
    const hasChildren = node.children && Array.isArray(node.children) && node.children.length > 0;

    return (
      <div key={node.id || level} className="tree-node" style={{ paddingLeft: paddingLeft }}>
        <div className="node-content">
          <span className="node-icon"><ChartBar size={16} /></span>
          <div className="node-info">
            <h4>{node.name || 'Unnamed'}</h4>
            <p className="code">{node.code || ''}</p>
          </div>
          <div className="node-balance">
            <p className="label">Balance</p>
            <p className="value">{'$' + balance}</p>
          </div>
        </div>
        {hasChildren && (
          <div className="children">
            {node.children.map((child, idx) => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading && !hierarchyTree) {
    return <div className="loading">Loading hierarchy...</div>;
  }

  return (
    <div className="account-hierarchy-builder">
      <div className="header">
        <h2><Tree size={20} /> Account Hierarchy Manager</h2>
        <button 
          className="btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          <PlusCircle size={16} /> New Hierarchy
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showCreateForm && (
        <form onSubmit={handleCreateHierarchy} className="create-hierarchy-form">
          <h3>Create Account Hierarchy</h3>
          <div className="form-group">
            <label>Hierarchy Name</label>
            <input
              type="text"
              value={formData.hierarchy_name}
              onChange={(e) => setFormData({...formData, hierarchy_name: e.target.value})}
              placeholder="e.g., Organizational Structure"
              required
            />
          </div>
          <div className="form-group">
            <label>Type</label>
            <select
              value={formData.hierarchy_type}
              onChange={(e) => setFormData({...formData, hierarchy_type: e.target.value})}
            >
              <option value="account_level">Account Level</option>
              <option value="organizational">Organizational</option>
              <option value="functional">Functional</option>
              <option value="program">Program</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Optional description"
            />
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary">Create</button>
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => setShowCreateForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {showNodeForm && activeHierarchy && (
        <form onSubmit={handleCreateNode} className="create-node-form">
          <h3>Add Node to Hierarchy</h3>
          <div className="form-group">
            <label>Node Name</label>
            <input
              type="text"
              value={nodeData.node_name}
              onChange={(e) => setNodeData({...nodeData, node_name: e.target.value})}
              placeholder="e.g., Finance Department"
              required
            />
          </div>
          <div className="form-group">
            <label>Node Code</label>
            <input
              type="text"
              value={nodeData.node_code}
              onChange={(e) => setNodeData({...nodeData, node_code: e.target.value})}
              placeholder="e.g., 1.1.1"
              required
            />
          </div>
          <div className="form-group">
            <label>Level</label>
            <select
              value={nodeData.level}
              onChange={(e) => setNodeData({...nodeData, level: e.target.value})}
            >
              <option value="detail">Detail (GL Account)</option>
              <option value="summary">Summary (Parent)</option>
              <option value="total">Total (Root)</option>
            </select>
          </div>
          {nodeData.level === 'DETAIL' && (
            <div className="form-group">
              <label>GL Account</label>
              <select
                value={nodeData.gl_account_id}
                onChange={(e) => setNodeData({...nodeData, gl_account_id: e.target.value})}
              >
                <option value="">Select Account</option>
                {glAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.account_number} - {account.account_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="form-actions">
            <button type="submit" className="btn-primary">Add Node</button>
            <button 
              type="button" 
              className="btn-secondary"
              onClick={() => setShowNodeForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {activeHierarchy && (
        <div className="hierarchy-controls">
          <button 
            className="btn-primary"
            onClick={() => setShowNodeForm(!showNodeForm)}
          >
            <PlusCircle size={16} /> Add Node
          </button>
          <button 
            className="btn-secondary"
            onClick={() => handleRollup(activeHierarchy.id)}
          >
            <ArrowsClockwise size={16} /> Recalculate Rollup
          </button>
          <button 
            className="btn-secondary"
            onClick={() => handleExport(activeHierarchy.id)}
          >
            <DownloadSimple size={16} /> Export
          </button>
        </div>
      )}

      {hierarchyTree && (
        <div className="hierarchy-display">
          <h3><ChartBar size={18} /> Hierarchy Structure</h3>
          <div className="tree-container">
            {renderTreeNode(hierarchyTree)}
          </div>
        </div>
      )}

      {consolidatedReport && (
        <div className="consolidated-report">
          <h3><TrendUp size={18} /> Consolidated Report by Level</h3>
          <div className="report-grid">
            {consolidatedReport.by_level && Object.entries(consolidatedReport.by_level).map(([level, data]) => (
              <div key={level} className="level-section">
                <h4>{level.toUpperCase()}</h4>
                <table className="level-table">
                  <thead>
                    <tr>
                      <th>Account</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.nodes && data.nodes.map((node, idx) => (
                      <tr key={idx}>
                        <td>{node.name}</td>
                        <td className="balance">{'$' + (node.balance ? parseFloat(node.balance).toFixed(2) : '0.00')}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td className="balance"><strong>{'$' + (data.total ? parseFloat(data.total).toFixed(2) : '0.00')}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          <div className="grand-total">
            <h4>Grand Total</h4>
            <p className="value">{'$' + (consolidatedReport.grand_total ? parseFloat(consolidatedReport.grand_total).toFixed(2) : '0.00')}</p>
          </div>
        </div>
      )}

      <div className="hierarchy-info">
        <h4><Lightbulb size={18} /> Account Hierarchy Benefits</h4>
        <ul>
          <li><Tree size={14} /> Multi-level account structures</li>
          <li><ArrowsClockwise size={14} /> Automatic balance rollup from detail to summary accounts</li>
          <li><ChartBar size={14} /> Consolidated reporting by organizational level</li>
          <li><Prohibit size={14} /> Circular reference prevention</li>
          <li><MagnifyingGlass size={14} /> Drill-down analysis capabilities</li>
        </ul>
      </div>
    </div>
  );
}

export default AccountHierarchyBuilder;
